import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo/erhr_logo.webp';
import { getUserOffice, ALL_OFFICES } from '../../config/locations';

const Footer: React.FC = () => {
  return (
    <footer className="bg-primary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src={logo} 
                alt="ERHR Global" 
                className="h-12 w-auto filter brightness-0 invert"
                width={120}
                height={48}
                loading="lazy"
              />
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Transforming organizations through strategic HR solutions. We provide comprehensive 
              transitional and strategic HR services to help your business thrive.
            </p>
            <div className="text-gray-300 text-sm mb-4 space-y-1">
              {ALL_OFFICES.map((office) => {
                const isNearest = office.region === getUserOffice().region;
                return (
                  <div key={office.region} className={`flex items-center ${isNearest ? 'text-white font-medium' : ''}`}>
                    <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{office.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/company/er-hr-global/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-200"
                aria-label="Follow us on LinkedIn"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/transitional-hr" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Transitional HR Services
                </Link>
              </li>
              <li>
                <Link 
                  to="/pricing" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Services &amp; Pricing
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/benefits-strategy" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Benefits Strategy
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/hr-systems" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  HR Systems
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/performance-management" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Performance Management
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/employee-engagement" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Employee Engagement
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/policies-procedures" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Policies &amp; Procedures
                </Link>
              </li>
              <li>
                <Link 
                  to="/strategic-hr/onboarding-offboarding" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Onboarding &amp; Offboarding
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/who-we-are" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Who We Are
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link 
                  to="/schedule-appointment" 
                  className="text-gray-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  Schedule Appointment
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} ERHR Global. All rights reserved.
          </p>
          <div className="flex items-center space-x-6 mt-4 md:mt-0">
            <Link 
              to="/privacy" 
              className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
